import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout/Layout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, RotateCcw, Trash2, Archive } from "lucide-react";
import { getTrashItems, restoreTrashItem, permanentlyDeleteTrashItem } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/authContext";
import { formatDistanceToNow, differenceInDays } from "date-fns";

function DaysUntilPurge({ deletedAt }: { deletedAt: string }) {
  const daysGone = differenceInDays(new Date(), new Date(deletedAt));
  const daysLeft = Math.max(0, 30 - daysGone);
  return (
    <span className="text-xs text-muted-foreground">
      {formatDistanceToNow(new Date(deletedAt), { addSuffix: true })}
      {daysLeft > 0 ? (
        <span className={`ml-1 ${daysLeft <= 7 ? "text-destructive font-medium" : ""}`}>
          · purges in {daysLeft}d
        </span>
      ) : (
        <span className="ml-1 text-destructive font-medium">· pending purge</span>
      )}
    </span>
  );
}

export default function Trash() {
  const { user } = useAuth();
  const isAdmin = (user as any)?.role === "admin";
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [tab, setTab] = useState("projects");

  const projectsQuery = useQuery({
    queryKey: ["trash", "projects"],
    queryFn: () => getTrashItems("projects"),
  });

  const tasksQuery = useQuery({
    queryKey: ["trash", "tasks"],
    queryFn: () => getTrashItems("tasks"),
  });

  const restoreMutation = useMutation({
    mutationFn: ({ type, id }: { type: "project" | "task"; id: string }) =>
      restoreTrashItem(type, id),
    onSuccess: (_, { type }) => {
      queryClient.invalidateQueries({ queryKey: ["trash"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast({ title: `${type === "project" ? "Project" : "Task"} restored` });
    },
    onError: () => toast({ title: "Restore failed", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: ({ type, id }: { type: "project" | "task"; id: string }) =>
      permanentlyDeleteTrashItem(type, id),
    onSuccess: (_, { type }) => {
      queryClient.invalidateQueries({ queryKey: ["trash"] });
      toast({ title: `${type === "project" ? "Project" : "Task"} permanently deleted` });
    },
    onError: () => toast({ title: "Delete failed", variant: "destructive" }),
  });

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-heading font-bold">Trash</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Deleted items are automatically purged after 30 days.
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="projects">Projects</TabsTrigger>
            <TabsTrigger value="tasks">Tasks</TabsTrigger>
            <TabsTrigger value="clients">Clients</TabsTrigger>
          </TabsList>

          {/* Projects tab */}
          <TabsContent value="projects" className="mt-4">
            {projectsQuery.isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : (projectsQuery.data ?? []).length === 0 ? (
              <div className="py-16 text-center text-sm text-muted-foreground">
                <Trash2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
                No deleted projects
              </div>
            ) : (
              <div className="rounded-lg border divide-y">
                {(projectsQuery.data ?? []).map((p: any) => (
                  <div key={p.id} className="flex items-center justify-between px-4 py-3 gap-4">
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{p.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{p.accounts?.account_name ?? "—"}</p>
                      {p.deleted_at && <DaysUntilPurge deletedAt={p.deleted_at} />}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        disabled={restoreMutation.isPending}
                        onClick={() => restoreMutation.mutate({ type: "project", id: p.id })}
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1" />
                        Restore
                      </Button>
                      {isAdmin && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive hover:text-destructive">
                              <Trash2 className="w-3.5 h-3.5 mr-1" />
                              Delete forever
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Permanently delete project?</AlertDialogTitle>
                              <AlertDialogDescription>
                                "{p.title}" and all its tasks will be permanently deleted. This cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className={buttonVariants({ variant: "destructive" })}
                                onClick={() => deleteMutation.mutate({ type: "project", id: p.id })}
                              >
                                Delete forever
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Tasks tab */}
          <TabsContent value="tasks" className="mt-4">
            {tasksQuery.isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : (tasksQuery.data ?? []).length === 0 ? (
              <div className="py-16 text-center text-sm text-muted-foreground">
                <Trash2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
                No deleted tasks
              </div>
            ) : (
              <div className="rounded-lg border divide-y">
                {(tasksQuery.data ?? []).map((t: any) => (
                  <div key={t.id} className="flex items-center justify-between px-4 py-3 gap-4">
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{t.title}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {t.projects?.title ?? "—"}
                        {t.projects?.accounts?.account_name ? ` · ${t.projects.accounts.account_name}` : ""}
                      </p>
                      {t.deleted_at && <DaysUntilPurge deletedAt={t.deleted_at} />}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        disabled={restoreMutation.isPending}
                        onClick={() => restoreMutation.mutate({ type: "task", id: t.id })}
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1" />
                        Restore
                      </Button>
                      {isAdmin && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive hover:text-destructive">
                              <Trash2 className="w-3.5 h-3.5 mr-1" />
                              Delete forever
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Permanently delete task?</AlertDialogTitle>
                              <AlertDialogDescription>
                                "{t.title}" will be permanently deleted. This cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className={buttonVariants({ variant: "destructive" })}
                                onClick={() => deleteMutation.mutate({ type: "task", id: t.id })}
                              >
                                Delete forever
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Clients tab — placeholder until accounts.deleted_at migration */}
          <TabsContent value="clients" className="mt-4">
            <div className="py-16 text-center text-sm text-muted-foreground">
              <Archive className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="font-medium text-foreground">Client archiving coming soon</p>
              <p className="mt-1">This requires a database migration. Contact your administrator to enable.</p>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
